/**
 * @file: MediaUploadQueue.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { LocalMediaSourceStatus, UploadedFile } from '@euler/model';
import { ImageFileExtraInfo, VideoFileExtraInfo } from '@euler/model/common';
import { FileUploadParameters, MediaFileService } from '@euler/services';
import { delayed, makeDebug, safeMarkDevFlag, sleep } from '@euler/utils';
import { manipulateAsync } from 'expo-image-manipulator';
import * as VideoThumbnails from 'expo-video-thumbnails';
import { Immutable } from 'immer';
import { nanoid } from 'nanoid/non-secure';
import { Platform } from 'react-native';
import {
  Image as ImageCompressor,
  Video as VideoCompressor,
  getVideoMetaData,
} from 'react-native-compressor';

const debug = makeDebug('fileuploader', false);

const kSimulateLatency = safeMarkDevFlag(false);
const kSimulatedLatencyInSeconds = 30;
const kSimulateConcurrency = safeMarkDevFlag(false);
const kSimulatedConcurrency = 1;

type SuccessResult =
  | {
      mediaType: 'image';
      result: UploadedFile<ImageFileExtraInfo>;
    }
  | {
      mediaType: 'video';
      result: UploadedFile<VideoFileExtraInfo>;
    };

export type MediaUploadResult = SuccessResult;

export type MediaUploadTaskEvent =
  | {
      type: 'queued';
    }
  | {
      type: 'begin';
    }
  | {
      type: 'compressing';
      progress: number;
    }
  | {
      type: 'progress';
      progress: number;
    }
  | {
      type: 'error';
      error: Error;
    }
  | ({
      type: 'finished';
    } & SuccessResult)
  | {
      type: 'abort';
      error?: Error;
    };

export type MediaUploadEventListener<TContext = unknown> = (
  e: MediaUploadTaskEvent,
  task: Immutable<MediaUploadTask<TContext>>,
) => void;

export type MediaUploadRequest<TContext = unknown> = {
  context?: TContext;
  localFileUri: string;
  contentType: string;
  type: 'image' | 'video';
  parameters?: FileUploadParameters;
  onSuccess?: (result: SuccessResult, context: TContext | undefined) => void;
  listener?: MediaUploadEventListener<TContext>;
  simulateFailure?: boolean;
};

export type MediaUploadTask<TContext = unknown> = {
  key: string;
  status: 'queued' | 'inprogress' | 'complete' | 'aborted';
  request: MediaUploadRequest<TContext>;
  abortController: AbortController;
};

const kDefaultConcurrency = 10;

export class MediaFileUploadController<TContext> {
  private readonly concurrency: number;
  private readonly mediaFileService: MediaFileService;

  private readonly _queue: MediaUploadTask<TContext>[] = [];
  private readonly _inprogressTasks = new Map<
    string,
    MediaUploadTask<TContext>
  >();
  private readonly _listeners: MediaUploadEventListener<TContext>[] = [];

  constructor({
    mediaFileService,
    concurrency = kSimulateConcurrency
      ? kSimulatedConcurrency
      : kDefaultConcurrency,
  }: {
    concurrency?: number;
    mediaFileService: MediaFileService;
  }) {
    this.concurrency = concurrency;
    this.mediaFileService = mediaFileService;
  }

  /**
   * Add event listener to the media file upload controller.
   * @param listener
   * @returns
   */
  addListener(listener: MediaUploadEventListener<TContext>): () => void {
    this._listeners.push(listener);
    return () => {
      const index = this._listeners.indexOf(listener);
      if (index >= 0) {
        this._listeners.splice(index, 1);
      }
    };
  }

  /**
   * Queue a media file upload task.
   * @param {MediaUploadRequest} request
   */
  enqueue<T extends TContext>(request: MediaUploadRequest<T>) {
    const task: MediaUploadTask<T> = {
      key: nanoid(),
      status: 'queued',
      request,
      abortController: new AbortController(),
    };
    debug('queued task:', task);
    this._queue.push(task as any);
    task.status = 'queued';
    this.emit({ type: 'queued' }, task as any);
    this.next();
  }

  /**
   * Request to abort the given task by the key if available.
   * @param {String} key key of the request
   */
  abort(key: string): boolean {
    let task = this._inprogressTasks.get(key);
    if (task) {
      task.status = 'aborted';
      task.abortController.abort();
      return true;
    }
    const index = this._queue.findIndex(x => x.key === key);
    if (index >= 0) {
      task = this._queue[index];
      this._queue.splice(index, 1);
      task.status = 'aborted';
      this.emit({ type: 'abort' }, task);
      debug('task aborted:', task);
      return true;
    }
    return false;
  }

  dispose() {
    debug('dispose');
    this._listeners.splice(0, this._listeners.length);
    for (const task of this._inprogressTasks.values()) {
      task.request = { ...task.request, listener: undefined };
      task.abortController.abort();
    }
    this._inprogressTasks.clear();
    this._queue.splice(0, this._queue.length);
  }

  private next() {
    if (this._inprogressTasks.size >= this.concurrency) {
      return;
    }

    const task = this._queue.shift();
    if (!task) return;

    void this.exec(task);
  }

  private emit(event: MediaUploadTaskEvent, task: MediaUploadTask<TContext>) {
    for (const listener of this._listeners) {
      listener(event, task as any);
    }
    delayed(() => {
      task.request.listener?.(event, task as any);
    }, 0);
  }

  private async exec(task: MediaUploadTask<TContext>) {
    debug('executing upload task:', task);

    if (task.request.simulateFailure) {
      await sleep(3000);
      debug('upload failed by simulating');
      task.status = 'complete';
      this.emit({ type: 'error', error: new Error('simulated error') }, task);
      this.next();
      return;
    }

    if (task.abortController.signal.aborted) {
      debug('task has been aborted');
      task.status = 'aborted';
      this.next();
      return;
    }

    this._inprogressTasks.set(task.key, task);

    task.status = 'inprogress';

    let localFileUriToBeUploaded = task.request.localFileUri;
    let compressVideoFileUri: string | undefined = undefined;
    let videoThumbnailUrl: string | undefined = undefined;

    if (/video/i.test(task.request.contentType) && Platform.OS !== 'web') {
      // compress the video if desired.
      try {
        if (__DEV__) {
          console.log(
            'getting video metadata before compress of video:',
            task.request.localFileUri,
          );
          const metadata = await getVideoMetaData(task.request.localFileUri);
          console.log('video metadata before compressing: ', metadata);
        }

        this.emit(
          {
            type: 'compressing',
            progress: 0,
          },
          task,
        );

        debug('compressing video... ');
        const startTime = performance.now();
        [compressVideoFileUri, videoThumbnailUrl] = await Promise.all([
          VideoCompressor.compress(
            task.request.localFileUri,
            {
              compressionMethod: 'auto',
              minimumFileSizeForCompress: 3,
            },
            progress => {
              if (__DEV__) {
                console.log('video compressing progress: ', progress);
              }
              this.emit(
                {
                  type: 'compressing',
                  progress,
                },
                task,
              );
            },
          ),
          VideoThumbnails.getThumbnailAsync(task.request.localFileUri, {
            quality: 0.8,
          })
            .then(async thumbnail => {
              try {
                // compress thumbnail
                let thumbnailFileUri = thumbnail.uri;

                if (task.request.parameters?.videoRotate) {
                  const rotatedThumbnail = await manipulateAsync(
                    thumbnailFileUri,
                    [
                      {
                        rotate: -task.request.parameters.videoRotate,
                      },
                    ],
                  );
                  thumbnailFileUri = rotatedThumbnail.uri;
                }

                try {
                  thumbnailFileUri = await ImageCompressor.compress(
                    thumbnailFileUri,
                    { compressionMethod: 'auto' },
                  );
                } catch (error) {
                  console.error(error);
                }

                const thumbnailUploadResult =
                  await this.mediaFileService.upload(thumbnailFileUri, {
                    blurhash: true,
                    mimeType: 'image/jpeg',
                  });
                if (__DEV__) {
                  console.log(
                    'uploaded video thumbnail: ',
                    thumbnailUploadResult,
                  );
                }
                return thumbnailUploadResult.url;
              } catch (e) {
                console.error(e);
                return undefined;
              }
            })
            .catch(err => {
              console.error(err);
              return undefined;
            }),
        ]);

        if (__DEV__) {
          console.log('getting video metadata after compress... ');
          const metadataAfter = await getVideoMetaData(compressVideoFileUri);
          console.log('video metadata after compressing: ', metadataAfter);
          console.log(`taken ${performance.now() - startTime} ms`);
        }

        localFileUriToBeUploaded = compressVideoFileUri;
      } catch (e) {
        console.error('Failed to compress video: ', e);
      }
    }

    this.emit(
      {
        type: 'begin',
      },
      task,
    );

    this.mediaFileService
      .upload(localFileUriToBeUploaded, {
        ...task.request.parameters,
        mimeType: task.request.contentType,
        videoThumbnailUrl,
        abortController: task.abortController,
        __delay__: kSimulateLatency
          ? kSimulatedLatencyInSeconds * 1000
          : undefined,
        onProgress: (e: ProgressEvent) => {
          debug('progress updated:', e, task);
          this.emit(
            {
              type: 'progress',
              progress: e.total > 0 ? e.loaded / e.total : 0,
            },
            task,
          );
        },
      })
      .then(result => {
        debug('upload success, result:', result, task);
        const data: SuccessResult = {
          mediaType: task.request.type === 'image' ? 'image' : 'video',
          result: result as any,
        };
        task.request.onSuccess?.(data, task.request.context);
        this.emit(
          {
            type: 'finished',
            ...data,
          },
          task,
        );
      })
      .catch(err => {
        if (err.name === 'AbortError') {
          __DEV__ && console.log('file upload task has been aborted:', task);
          this.emit({ type: 'abort', error: err }, task);
        } else {
          console.error('an error occurred while uploading media file: ', err);
          this.emit({ type: 'error', error: err }, task);
        }
      })
      .finally(() => {
        task.status = 'complete';
        this._inprogressTasks.delete(task.key);
        this.next();
      });
  }
}

export const mediaUploadEventTypeToStatus = (
  event: MediaUploadTaskEvent,
): LocalMediaSourceStatus | undefined => {
  switch (event.type) {
    case 'queued':
      return 'queued';
    case 'progress':
      return 'uploading';
    case 'finished':
      return 'uploaded';
    case 'compressing':
      return 'compressing';
    case 'error':
      return 'error';
    default:
      return undefined;
  }
};
